import * as d3 from 'd3'
import _ from 'underscore'
import { timelineSpanHasError } from '../../domain/TimelineSpan'

let Chart = function() {
    var svg = null

    var brush = null

    var dimensions = {
        width: 0,
        height: 0,
    }

    var margin = {
        top: 25,
        right: 5,
        bottom: 20,
        left: 250,
    }

    var barSize = {
        height: 26,
        padding: 11,
    }

    var eventSize = {
        height: 13,
        padding: 4,
    }

    var spans = null
    var ticks = null
    var categories = null
    var selected = null
    var selectedEvent = null
    var scales = null
    var plotValuesCallback = null
    var tickFormatCallback = null
    var globalEvents = null
    var traceBounds = null

    const hideTraceBoundsTooltip = () => {
        d3.select(svg)
            .select('.trace-bounds-tooltip')
            .style('visibility', 'hidden')
    }

    const showTraceBoundsTooltip = (traceBounds, traceBoundsNode) => {
        const tooltip = d3.select(svg).select('.trace-bounds-tooltip')
        const position = { x: scales.x(traceBounds.xStart) + 5 + margin.left, y: d3.mouse(traceBoundsNode)[1] + margin.top }

        tooltip.selectAll('*').remove()

        const rect = tooltip.append('rect')
        const text = tooltip
            .append('text')
            .attr('x', position.x)
            .attr('y', position.y)

        text.append('tspan')
            .attr('x', text.attr('x'))
            .attr('dy', 0)
            .style('font-weight', 'bold')
            .text('Distributed Tracing')

        text.append('tspan')
            .attr('x', text.attr('x'))
            .attr('dy', '1.2em')
            .text('These spans belong to a child trace')

        text.append('tspan')
            .attr('x', text.attr('x'))
            .attr('dy', '1.2em')
            .text(`served by ${traceBounds.serverName}`)

        const bbox = text.node().getBBox()

        rect.attr('x', position.x - bbox.width - 30)
            .attr('y', position.y - 15)
            .attr('height', bbox.height + 5)
            .attr('width', bbox.width + 10)
            .attr('fill', 'white')
            .attr('stroke', 'gray')
            .attr('stroke-width', 1)
            .attr('opacity', 0.9)

        text.attr('x', position.x - bbox.width - 25)
            .selectAll('tspan')
            .attr('x', position.x - bbox.width - 25)

        tooltip.style('visibility', null)
    }

    const hideGlobalEventTooltip = eventNode => {
        d3.select(eventNode)
            .select('line.marker')
            .attr('stroke-dasharray', '5,5')
        d3.select(svg)
            .select('.eventInfoBox')
            .style('visibility', 'hidden')
    }

    const showGlobalEventTooltip = (event, eventNode) => {
        const tooltip = d3.select(svg).select('.eventInfoBox')
        const position = { x: scales.x(event.ts) + 5 + margin.left, y: d3.mouse(eventNode)[1] + margin.top }
        const timeHumanReadable = event.ts > 1000 * 1000 ? Math.round(event.ts / 1000) / 1000 + 's' : Math.round(event.ts / 1000) + 'ms'
        const additionalInformation = {
            fastcgi_finish_request: [
                'PHP completed sending headers+body back ',
                'to the webserver here. Response time after',
                'this point is not added to the users response time.',
            ],
            session_write_close: [
                'After this point the session is closed for changes, ',
                'unlocking it and allowing other requests using ',
                'the same session to proceed.',
            ],
            'mem-limit': [
                'Tideways stopped detailed tracing at this point,',
                'to avoid triggering PHPs maximum memory limit.',
                'This is controlled by the php.ini setting',
                '"tideways.disable_at_memory_percentage". You can also',
                'increase PHPs "memory_limit" setting to avoid this limit.',
            ],
            'tracepoint-start': [
                'A tracepoint matches at this point in the request and ',
                'more profiling data is collected from here on.',
            ],
            'time-limit': [
                'Tideways stopped detailed tracing at this point.',
                'to avoid collecting too much data.',
                'This is controlled by the php.ini setting ',
                '"tideways.traces_max_seconds" that defaults to 60 seconds.',
            ],
        }

        let eventName = event.name
        let matches
        if ((matches = eventName.match(/^invalidate cache: (.*)/))) {
            eventName = 'invalidate-cache'
            additionalInformation[eventName] = [
                ' ',
                'Items invalidated: ',
                ...matches[1].split(',').map(line => '* ' + line.trim()),
                ' ',
                'The listed cache tags have been invalidated',
                'during the execution of this request.',
            ]
        }

        tooltip.selectAll('*').remove()

        const rect = tooltip.append('rect')
        const text = tooltip
            .append('text')
            .attr('x', position.x)
            .attr('y', position.y)

        text.append('tspan')
            .attr('x', text.attr('x'))
            .attr('dy', 0)
            .style('font-weight', 'bold')
            .text(eventName + ' @' + timeHumanReadable)

        if (eventName in additionalInformation) {
            additionalInformation[eventName].forEach(line =>
                text
                    .append('tspan')
                    .attr('x', text.attr('x'))
                    .attr('dy', 20)
                    .text(line),
            )
        }

        const bbox = text.node().getBBox()

        rect.attr('x', position.x - bbox.width - 30)
            .attr('y', position.y - 15)
            .attr('height', bbox.height + 10)
            .attr('width', bbox.width + 10)
            .attr('fill', 'white')
            .attr('stroke', 'gray')
            .attr('stroke-width', 1)
            .attr('opacity', 0.8)

        text.attr('x', position.x - bbox.width - 25)
            .selectAll('tspan')
            .attr('x', position.x - bbox.width - 25)

        tooltip.style('visibility', null)

        d3.select(eventNode)
            .select('line.marker')
            .attr('stroke-dasharray', null)
    }

    this.create = function(rootElement, width, _plotValuesCallback, _tickFormatCallback) {
        svg = rootElement
        dimensions.width = width

        plotValuesCallback = _plotValuesCallback
        tickFormatCallback = _tickFormatCallback

        let d3Svg = d3
            .select(svg)
            .attr('class', 'chart')
            .attr('width', dimensions.width)

        let container = d3Svg.append('g').attr('class', 'container')

        let defs = container.append('defs')
        let pattern = defs
            .append('pattern')
            .attr('id', 'stripe')
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 40)
            .attr('height', 20)

        pattern
            .append('line')
            .attr('x1', 10)
            .attr('y1', 0)
            .attr('x2', 30)
            .attr('y2', 20)
        pattern
            .append('line')
            .attr('x1', -10)
            .attr('y1', 0)
            .attr('x2', 10)
            .attr('y2', 20)
        pattern
            .append('line')
            .attr('x1', 30)
            .attr('y1', 0)
            .attr('x2', 50)
            .attr('y2', 20)

        defs.append('mask')
            .attr('id', 'mask')
            .append('rect')
            .attr('height', 100)
            .attr('width', 1200)
            .style('fill', 'url(#stripe)')

        // Create dotted pattern for mask
        let dottedPattern = defs
            .append('pattern')
            .attr('id', 'dotted-pattern')
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 8)
            .attr('height', 8)

        dottedPattern
            .append('rect')
            .attr('width', 8)
            .attr('height', 8)
            .attr('fill', 'white')

        dottedPattern
            .append('circle')
            .attr('cx', 2)
            .attr('cy', 2)
            .attr('r', 1.5)
            .attr('fill', 'black')

        dottedPattern
            .append('circle')
            .attr('cx', 6)
            .attr('cy', 6)
            .attr('r', 1.5)
            .attr('fill', 'black')

        defs.append('mask')
            .attr('id', 'mask-dotted')
            .append('rect')
            .attr('height', 100)
            .attr('width', 1200)
            .style('fill', 'url(#dotted-pattern)')

        let axis = container.append('g').attr('class', 'axis')

        axis.append('g').attr('class', 'y')
        axis.append('g').attr('class', 'x')

        container
            .append('g')
            .attr('class', 'selected-row-borders')
            .style('pointer-events', 'none')

        let brushGroup = container
            .append('g')
            .attr('class', 'brush')
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        brushGroup
            .append('rect')
            .attr('class', 'background')
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', 6)

        traceBounds = container.append('g').attr('class', 'trace-bounding-boxes')

        spans = container.append('g').attr('class', 'spans')

        globalEvents = container.append('g').attr('class', 'global-events')

        container
            .append('g')
            .attr('class', 'eventInfoBox')
            .style('visibility', 'hidden')

        container
            .append('g')
            .attr('class', 'trace-bounds-tooltip')
            .style('visibility', 'hidden')

        container.append('g').attr('class', 'sticky-elements')

        _drawClearButton(container)
    }

    var _getTickFormat = function(d) {
        return tickFormatCallback ? tickFormatCallback(d) : (d / 1000).toFixed() + 'ms'
    }

    var _getSpanDimension = function(span) {
        return plotValuesCallback ? plotValuesCallback(span) : { s: span.cs, t: span.wt }
    }

    const _getFirstSpanForTick = tickId => spans.find(span => span.tick === tickId)

    const _selectFirstSpanForTick = tick => {
        if (d3.event.button !== 0) {
            return
        }

        const firstSpan = _getFirstSpanForTick(tick.id)
        if (!firstSpan) {
            return
        }

        jQuery(svg).trigger('spanSelected', firstSpan)
    }

    var _drawClearButton = function(container) {
        let button = container
            .append('g')
            .attr('class', 'button hidden')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', margin.left)
            .attr('height', margin.top)

        button
            .append('rect')
            .attr('class', 'shadow')
            .attr('width', 16)
            .attr('height', 16)
            .attr('x', margin.left - 16 - 16 - 1)
            .attr('y', 2 + 2)

        button
            .append('rect')
            .attr('class', 'bg')
            .attr('width', 16)
            .attr('height', 16)
            .attr('x', margin.left - 16 - 16 - 2)
            .attr('y', 1 + 2)

        button
            .append('text')
            .attr('class', 'button icon')
            .attr('x', margin.left - 8 - 16 - 2)
            .attr('y', 13 + 2)
            .attr('text-anchor', 'middle')
            .text('\uf010')
    }

    var _scales = function(spans, ticks) {
        let maxTime = _.max(
            _.map(spans, function(span) {
                let dim = _getSpanDimension(span)

                return dim.s + dim.t
            }),
        )
        let yOffset = 0

        const params = new URLSearchParams(window.location.search)
        if (params.has('max_time')) {
            maxTime = parseInt(params.get('max_time'), 10)
        }

        return {
            x: d3
                .scaleLinear()
                .domain([0, maxTime])
                .range([0, dimensions.width - margin.left - margin.right])
                .clamp(true),
            x2: d3
                .scaleLinear()
                .domain([0, maxTime])
                .range([0, dimensions.width - margin.left - margin.right]),
            y: d3
                .scaleOrdinal()
                .domain(_.map(ticks, tick => tick.id))
                .range(
                    _.toArray(
                        _.map(ticks, function(tick) {
                            let yPosition = yOffset
                            yOffset += tick.height
                            return yPosition
                        }),
                    ),
                ),
            colorsGlobalEvents: d3.scaleOrdinal(d3.schemeCategory10),
        }
    }

    var _axis = function(scales) {
        return {
            x: d3.axisTop(scales.x).tickFormat(_getTickFormat),
            // .tickSubdivide(true),
            y: d3.axisLeft(scales.y).tickSize(0),
        }
    }

    var _addClass = function(nodes, className) {
        nodes.each(function() {
            var node = d3.select(this)

            if (node.attr('class').indexOf(className) < 0) {
                node.attr('class', node.attr('class') + ' ' + className)
            }
        })
    }

    var _removeClass = function(nodes, className) {
        nodes.each(function() {
            var node = d3.select(this)

            if (node.attr('class').indexOf(className) >= 0) {
                node.attr('class', node.attr('class').replace(className, ''))
            }
        })
    }

    var wrap = function() {
        var width = margin.left
        var padding = 3

        var that = d3.select(this)
        var textLength = that.node().getComputedTextLength()
        var text = that.text()

        that.attr('data-original', text)

        while (textLength > width - 2 * padding && text.length > 0) {
            text = text.slice(1)
            that.text('...' + text)
            textLength = that.node().getComputedTextLength()
        }
    }

    var _drawAxis = function(scales, axis, ticks) {
        var root = d3.select(svg).select('.axis')
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        // Custom rendering for Y axis
        var yAxis = root.selectAll('.y')
        var row = yAxis.selectAll('.tick').data(_.values(ticks), function(tick) {
            return 't_' + tick.id
        })
        var newRow = row.enter()
        var group = newRow.append('g').attr('id', function(tick) {
            return 't_' + tick.id
        })

        // New node
        group
            .attr('transform', function(tick) {
                return 'translate(0, ' + scales.y(tick.id) + ')'
            })
            .attr('class', function() {
                return 'tick'
            })
            .on('mouseover', function(tick) {
                jQuery(svg).trigger('tickHover', tick)
            })
            .on('mouseout', function(tick) {
                jQuery(svg).trigger('tickHoverExit', tick)
            })
            .on('click', function(tick) {
                _selectFirstSpanForTick(tick)
            })

        group
            .append('rect')
            .attr('class', 'background')
            .attr('x', -margin.left)
            .attr('y', 0)
            .attr('width', dimensions.width - margin.right)
            .attr('height', function(tick) {
                return tick.height
            })

        group
            .append('text')
            .attr('x', -5)
            .attr('y', tick => tick.height / 2)
            .attr('dominant-baseline', 'middle')
            .text(tick => tick.summary)
            .each(wrap)

        // Update existing node (set properties which might change)
        yAxis
            .selectAll('.tick')
            .attr(
                'class',
                tick =>
                    'tick ' +
                    (tick.odd ? 'odd' : 'even') +
                    (tick.hasBottleneck ? ' hasBottleneck' : '') +
                    (tick.selected ? ' selected' : ''),
            )
            .attr('transform', function(tick) {
                if (scales.y(tick.id) === undefined) {
                    return 'translate(0, 0)'
                }

                return 'translate(0, ' + scales.y(tick.id) + ')'
            })

        yAxis.selectAll('.background').attr('height', function(tick) {
            return tick.height
        })
        // Exiting node
        row.exit().remove()

        // Just native rendering for X axis
        root.selectAll('.x').call(axis.x)

        root.selectAll('.x .tick line').attr('y1', dimensions.height - margin.top - margin.bottom)

        _drawStickyHeader()
        _drawSelectedRowBorders(scales, ticks)
    }

    const _drawSelectedRowBorders = (scales, ticks) => {
        var root = d3.select(svg).select('.selected-row-borders')
        var selectedTicks = Object.values(ticks).filter(tick => tick.selected)

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        var border = root.selectAll('.selected-row-border').data(selectedTicks, function(tick) {
            return 'selected_tick_' + tick.id
        })
        var borderGroup = border
            .enter()
            .append('g')
            .attr('class', 'selected-row-border')

        borderGroup.append('line').attr('class', 'row-border row-border-top')
        borderGroup.append('line').attr('class', 'row-border row-border-bottom')

        borderGroup = borderGroup.merge(border)

        borderGroup
            .select('.row-border-top')
            .attr('x1', -margin.left)
            .attr('x2', dimensions.width - margin.right - margin.left)
            .attr('y1', tick => scales.y(tick.id) + 0.5)
            .attr('y2', tick => scales.y(tick.id) + 0.5)

        borderGroup
            .select('.row-border-bottom')
            .attr('x1', -margin.left)
            .attr('x2', dimensions.width - margin.right - margin.left)
            .attr('y1', tick => scales.y(tick.id) + tick.height - 0.5)
            .attr('y2', tick => scales.y(tick.id) + tick.height - 0.5)

        border.exit().remove()
    }

    var _drawStickyHeader = function() {
        let stickyGroup = d3.select(svg).select('.sticky-elements')

        const xAxis = d3.select(svg).select('.axis .x')
        const xAxisClone = stickyGroup.selectAll('.x-clone').data([null])

        const xAxisCloneEnter = xAxisClone
            .enter()
            .append('g')
            .attr('class', 'x-clone')
            .attr('text-anchor', 'middle')
            .style('pointer-events', 'none')

        const xAxisCloneMerged = xAxisCloneEnter.merge(xAxisClone)

        xAxisCloneMerged.selectAll('*').remove()

        // Add white rectangle for background
        xAxisCloneMerged
            .append('rect')
            .attr('class', 'clone-background')
            .attr('x', -margin.left)
            .attr('y', -50)
            .attr('width', dimensions.width)
            .attr('height', 60)
            .attr('fill', 'white')

        // Copy text elements from .x to .x-clone, but without the axis line
        xAxis.selectAll('.tick').each(function() {
            const node = d3.select(this)
            const clonedGroup = xAxisCloneMerged.append('g')
            clonedGroup.attr('class', node.attr('class'))
            clonedGroup.attr('transform', node.attr('transform'))

            node.selectAll('text').each(function() {
                const textNode = d3.select(this)
                const clonedText = clonedGroup.append('text')
                Array.from(this.attributes).forEach(attr => {
                    clonedText.attr(attr.name, attr.value)
                })
                clonedText.text(textNode.text())
            })
        })

        let animationFrameId = null

        const updateStickyPosition = () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId)
            }

            animationFrameId = requestAnimationFrame(() => {
                const svgRect = svg.getBoundingClientRect()
                const scrollableParent = svg.parentElement
                const parentRect = scrollableParent.getBoundingClientRect()
                const scrollY = scrollableParent.scrollTop
                const chartTop = svgRect.top - parentRect.top + scrollY
                const shouldStick = scrollY > chartTop

                if (shouldStick) {
                    const offsetY = scrollY - chartTop + margin.top
                    xAxisCloneMerged.attr('transform', `translate(${margin.left}, ${offsetY})`).style('visibility', 'visible')
                } else {
                    xAxisCloneMerged.attr('transform', `translate(${margin.left}, ${margin.top})`).style('visibility', 'hidden')
                }
            })
        }

        updateStickyPosition()

        const scrollableParent = svg.parentElement
        scrollableParent.removeEventListener('scroll', updateStickyPosition)
        scrollableParent.addEventListener('scroll', updateStickyPosition)
    }

    var _drawSpans = function(scales, categories, spans, selected) {
        var root = d3.select(svg).selectAll('.spans')
        var selectedSpan = spans.find(span => span.id === selected)
        var selectedTick = selectedSpan ? selectedSpan.tick : null

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        root.select('.background')
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)

        var span = root.selectAll('.span').data(spans, function(span) {
            return 's_' + span.id
        })
        var newSpan = span.enter()
        var group = newSpan
            .append('g')
            .attr('class', 'span')
            .attr('id', function(span) {
                return 's_' + span.id
            })

        // New node
        group
            .attr('transform', function(span) {
                return 'translate(0, ' + scales.y(span.tick) + ')'
            })
            .on('mouseover', function(span) {
                jQuery(svg).trigger('spanHover', span)
            })
            .on('mouseout', function(span) {
                jQuery(svg).trigger('spanHoverExit', span)
            })
            .on('click', function(span) {
                _removeClass(d3.select(svg).selectAll('.spans .span'), 'selected')
                _addClass(d3.select(this), 'selected')

                jQuery(svg).trigger('spanSelected', span)
            })

        group.append('rect').attr('class', 'bar')

        group
            .append('text')
            .filter(timelineSpanHasError)
            .attr('class', 'icon')
            .attr('transform', 'translate(2, ' + (barSize.height - 10) + ')')
            .text('\uf0e7')

        // selection logic changed in D3 v4
        //   we want to have old and new nodes from here on
        var newAndUpdated = group.merge(span)
        newAndUpdated.attr(
            'class',
            span => 'span' + (selectedTick && span.tick === selectedTick ? ' active-line' : '') + (selected === span.id ? ' selected' : ''),
        )

        // This enters the third dimension – datasets of datasets
        var eventRoot = newAndUpdated.selectAll('.event').data(
            function(span) {
                return _.map(span.e || [], function(event) {
                    event.span = span
                    event.active = !!event.span.events
                    return event
                })
            },
            function(event) {
                return 'e_' + event.span.id + '_' + event.cs
            },
        )

        _drawEvents(eventRoot, categories, scales)

        // Update existing node (set properties which might change)
        newAndUpdated
            .transition()
            .duration(50)
            .attr('transform', function(span) {
                let dim = _getSpanDimension(span)
                return 'translate(' + scales.x(dim.s) + ', ' + scales.y(span.tick) + ')'
            })
            .attr('mask', function(span) {
                if (span.annotations && 'repeat_count' in span.annotations) {
                    return 'url(#mask)'
                } else if (span.async) {
                    return 'url(#mask-dotted)'
                }
                return null
            })

        newAndUpdated
            .select('.bar')
            .transition()
            .duration(50)
            .attr('transform', 'translate(0, ' + barSize.padding / 2 + ')')
            .attr('height', function() {
                return barSize.height - barSize.padding
            })
            .attr('width', function(span) {
                const dim = _getSpanDimension(span)
                const width = scales.x(dim.t + dim.s) - scales.x(dim.s)

                const minWidth = timelineSpanHasError(span) ? 10 : 5

                return width <= 0 ? 0 : Math.max(width, minWidth)
            })
            .attr('class', function(span) {
                let category = null
                if (span.cat in categories) {
                    category = categories[span.cat].className;
                } else {
                    const components = span.cat.split(/\./g)
                    if (components[0] in categories) {
                        category = categories[components[0]].className;
                    }
                }

                return `bar ${category ? `category-${category}` : ''}`
            })

        // Exiting node
        span.exit().remove()
    }

    const _drawGlobalEvents = (scales, globalEvents) => {
        let root = d3.select(svg).selectAll('.global-events')

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let event = root.selectAll('.global-event').data(globalEvents, event => 'ga_' + event.ts)
        let newEvent = event.enter()
        let group = newEvent
            .append('g')
            .attr('class', 'global-event')
            .attr('id', event => 'ga_' + event.ts)
            .on('mouseover', function(event) {
                showGlobalEventTooltip(event, this)
            })
            .on('mouseout', function() {
                hideGlobalEventTooltip(this)
            })

        const outOfBounds = event => scales.x(event.ts + 1000) - scales.x(event.ts) > 0.0
        const hitBoxMargin = 10
        const height = dimensions.height - margin.top - margin.bottom

        // New node
        group
            .append('rect')
            .attr('width', 2 * hitBoxMargin + 1)
            .attr('opacity', 0)
            .attr('class', 'hover-hitbox')
            .attr('height', event => height - scales.y(event.startY))
            .attr('transform', event => 'translate(' + (scales.x(event.ts) - hitBoxMargin) + ', ' + scales.y(event.startY) + ')')

        group
            .append('line')
            .attr('stroke', event => scales.colorsGlobalEvents(event.name))
            .attr('stroke-dasharray', '5,5')
            .attr('class', 'marker')
            .attr('opacity', event => (outOfBounds(event) ? 1 : 0))
            .attr('y1', event => scales.y(event.yStart) + barSize.padding / 2 + 1)
            .attr('y2', height)
            .attr('transform', event => 'translate(' + scales.x(event.ts) + ', 0)')

        const newAndUpdated = event.merge(group)

        // Update existing nodes
        newAndUpdated
            .select('line.marker')
            .transition()
            .duration(50)
            .attr('y1', event => scales.y(event.yStart) + barSize.padding / 2 + 1)
            .attr('y2', height)
            .attr('transform', event => 'translate(' + scales.x(event.ts) + ', 0)')
            .attr('opacity', event => (outOfBounds(event) ? 1 : 0))

        newAndUpdated
            .select('rect.hover-hitbox')
            .transition()
            .duration(50)
            .attr('height', event => height - scales.y(event.startY))
            .attr('transform', event => 'translate(' + (scales.x(event.ts) - hitBoxMargin) + ', ' + scales.y(event.startY) + ')')
            .attr('width', event => (outOfBounds(event) ? 2 * hitBoxMargin + 1 : 0))

        // Exiting node
        event.exit().remove()
    }

    const _drawTraceBounds = (scales, traceBounds) => {
        let root = d3.select(svg).selectAll('.trace-bounding-boxes')

        const yPositionStart = boundingBox => scales.y(boundingBox.yStart) + barSize.padding / 2 + 1
        const yPositionEnd = boundingBox => scales.y(boundingBox.yEnd) + barSize.height
        const height = boundingBox => yPositionEnd(boundingBox) - yPositionStart(boundingBox)

        // move the bounding into the chart area (away from the summaries)
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let box = root.selectAll('.trace-bounding-box').data(traceBounds, boundingBox => 'bb_' + boundingBox.traceId)
        let newBox = box.enter()
        let group = newBox
            .append('g')
            .attr('class', 'trace-bounding-box')
            .attr('id', boundingBox => 'bb_' + boundingBox.traceId)
            .on('mouseover', function(event) {
                showTraceBoundsTooltip(event, this)
            })
            .on('mouseout', function() {
                hideTraceBoundsTooltip()
            })

        // New node
        group
            .append('rect')
            .attr('opacity', 0.1)
            .attr('fill', 'gray')
            .attr('class', 'trace-bounding-box-background')
            .attr('width', boundingBox => scales.x(boundingBox.xEnd) - scales.x(boundingBox.xStart))
            .attr('height', height)
            .attr('transform', boundingBox => 'translate(' + scales.x(boundingBox.xStart) + ', ' + yPositionStart(boundingBox) + ')')

        group
            .append('line')
            .attr('stroke', 'black')
            .attr('class', 'trace-bounding-box-start')
            .attr('y1', yPositionStart)
            .attr('y2', yPositionEnd)
            .attr('transform', boundingBox => 'translate(' + scales.x(boundingBox.xStart) + ', 0)')

        group
            .append('line')
            .attr('stroke', 'black')
            .attr('class', 'trace-bounding-box-end')
            .attr('y1', yPositionStart)
            .attr('y2', yPositionEnd)
            .attr('transform', boundingBox => 'translate(' + scales.x(boundingBox.xEnd) + ', 0)')

        const boxNewAndUpdated = box.merge(group)

        // Update existing nodes
        boxNewAndUpdated
            .select('rect.trace-bounding-box-background')
            .transition()
            .duration(50)
            .attr('width', boundingBox => scales.x(boundingBox.xEnd) - scales.x(boundingBox.xStart))
            .attr('height', height)
            .attr('transform', boundingBox => 'translate(' + scales.x(boundingBox.xStart) + ', ' + yPositionStart(boundingBox) + ')')

        boxNewAndUpdated
            .select('line.trace-bounding-box-start')
            .transition()
            .duration(50)
            .attr('y1', yPositionStart)
            .attr('y2', yPositionEnd)
            .attr('transform', boundingBox => 'translate(' + scales.x(boundingBox.xStart) + ', 0)')

        boxNewAndUpdated
            .select('line.trace-bounding-box-end')
            .transition()
            .duration(50)
            .attr('y1', yPositionStart)
            .attr('y2', yPositionEnd)
            .attr('transform', boundingBox => 'translate(' + scales.x(boundingBox.xEnd) + ', 0)')

        // Exiting node
        box.exit().remove()
    }

    var _drawEvents = function(root, categories, scales) {
        var newEvent = root.enter()
        var group = newEvent.append('g').attr('class', 'event')

        // New node
        group
            .on('mouseover', function(event) {
                jQuery(svg).trigger('eventHover', [event.span, event])
            })
            .on('mouseout', function(event) {
                jQuery(svg).trigger('eventHoverExit', [event.span, event])
            })
            .on('click', function(event) {
                // Required, otherwise spanSelected will be triggered right
                // afterwards, which de-selects the event again
                d3.event.stopPropagation()

                jQuery(svg).trigger('eventSelected', [event.span, event])
            })

        group.append('rect').attr('class', 'bar')

        group = group.merge(root)

        group
            .select('.bar')
            .transition()
            .duration(50)
            .attr('class', function(event) {
                let category = ''
                if (event.cat && event.cat in categories) {
                    category = ' category-' + categories[event.cat].className
                }

                return 'bar' + (event.active ? ' active' + category : '') + (event.cs === selectedEvent ? ' selected' : '')
            })
            .attr('x', function(event) {
                let spanPlot = plotValuesCallback(event.span)
                let eventPlot = plotValuesCallback(event.span, event)
                return scales.x(eventPlot.s) - scales.x(spanPlot.s)
            })
            .attr('y', function(event) {
                return (
                    (event.active ? (event.span.events.row(event) - 2) * eventSize.height + barSize.height + eventSize.padding : 0) +
                    barSize.padding / 2 +
                    2.5
                )
            })
            .attr('height', function() {
                return eventSize.height - eventSize.padding
            })
            .attr('width', function(event) {
                let eventPlot = plotValuesCallback(event.span, event)
                return scales.x(eventPlot.s + eventPlot.t) - scales.x(eventPlot.s)
            })

        // Exiting node
        root.exit().remove()
    }

    var _touches = function(a, b) {
        return (
            (a.cs > b.cs && a.cs < b.cs + b.wt) ||
            (a.cs + a.wt > b.cs && a.cs + a.wt < b.cs + b.wt) ||
            (b.cs > a.cs && b.cs < a.cs + a.wt) ||
            (b.cs + b.wt > a.cs && b.cs + b.wt < a.cs + a.wt)
        )
    }

    var _fitEvents = function(span) {
        let processed = []
        let maxRow = 0
        let events = _.sortBy(span.e, function(event) {
            return event.cs
        })

        for (let i = 0; i < events.length; ++i) {
            let row = 1
            for (let j = 0; j < processed.length; ++j) {
                if (_touches(events[i], processed[j])) {
                    row = Math.max(row, processed[j].row + 1)
                }
            }

            maxRow = Math.max(maxRow, row)
            events[i].row = row
            processed.push(events[i])
        }

        span.e = processed
        return {
            height: maxRow * eventSize.height,
            row: function(event) {
                return event.row
            },
        }
    }

    this.draw = function() {
        brush.clear(d3.select(svg).select('.brush'))

        _drawAxis(scales, _axis(scales), ticks)
        _drawTraceBounds(scales, traceBounds)
        _drawSpans(scales, categories, spans, selected)
        _drawGlobalEvents(scales, globalEvents)
        this.scrollSelectedRowIntoView()
    }

    this.isZoomedIn = function() {
        if (!scales) {
            return false
        }

        const currentDomain = scales.x.domain()
        const fullDomain = scales.x2.domain()

        return currentDomain[0] !== fullDomain[0] || currentDomain[1] !== fullDomain[1]
    }

    this.isSpanVisible = function(span) {
        if (!scales) {
            return true
        }

        const dim = _getSpanDimension(span)
        const [viewportStart, viewportEnd] = scales.x.domain()

        return dim.s <= viewportEnd && dim.s + dim.t >= viewportStart
    }

    this.resetZoom = function() {
        if (!scales) {
            return
        }

        d3.select(svg)
            .select('.button')
            .attr('class', 'button hidden')
        scales.x.domain(scales.x2.domain())
    }

    this.scrollSelectedRowIntoView = function() {
        if (!scales || !selected || !svg || !svg.parentElement) {
            return
        }

        const selectedTick = ticks.find(tick => tick.selected)

        if (!selectedTick) {
            return
        }

        const scrollableParent = svg.parentElement
        const rowTop = margin.top + scales.y(selectedTick.id)
        const rowBottom = rowTop + selectedTick.height
        const viewportTop = scrollableParent.scrollTop
        const viewportBottom = viewportTop + scrollableParent.clientHeight
        const stickyHeaderOffset = margin.top

        if (rowTop < viewportTop + stickyHeaderOffset) {
            scrollableParent.scrollTop = Math.max(0, rowTop - stickyHeaderOffset)
            return
        }

        if (rowBottom > viewportBottom) {
            scrollableParent.scrollTop = rowBottom - scrollableParent.clientHeight
        }
    }

    this.zoomToSelection = function(selection) {
        // The domain value indicates the zoom level,
        // If both values are equal, there is no way to zoom in.
        const isDomainValueIdentical = xDomain => {
            const first = _getTickFormat(xDomain[0]).replace('ms', '')
            const second = _getTickFormat(xDomain[1]).replace('ms', '')
            return first === second
        }

        if (!selection) {
            return
        }

        if (isDomainValueIdentical(scales.x.domain())) {
            brush.clear(d3.select(svg).select('.brush'))
            return
        }

        if (scales !== null) {
            scales.x.domain([scales.x.invert(selection[0]), scales.x.invert(selection[1])])
        }

        d3.select(svg)
            .select('.button')
            .attr('class', 'button')

        this.draw()
    }

    this.brushEnd = function() {
        const { selection } = d3.event
        this.zoomToSelection(selection)
    }

    this.update = function(
        spansInput,
        ticksInput,
        globalEventsInput,
        traceBoundsInput,
        categoriesInput,
        selectedInput,
        selectedEventInput,
        refreshScales,
    ) {
        spans = spansInput
        ticks = ticksInput
        globalEvents = globalEventsInput
        traceBounds = traceBoundsInput
        categories = categoriesInput
        selected = selectedInput
        selectedEvent = selectedEventInput

        let selectedSpan = _.findWhere(spans, { id: selected })
        let odd = false
        for (let i = 0; i < spans.length; ++i) {
            spans[i].events = null
        }

        for (let i = 0; i < ticks.length; ++i) {
            let tickId = ticks[i].id
            let selected = selectedSpan && tickId === selectedSpan.tick
            let span = _.findWhere(spans, { tick: tickId })
            let unfoldEvents = true
            if (unfoldEvents) {
                span.events = _fitEvents(span)
            }

            ticks[i].selected = selected
            ticks[i].odd = odd
            ticks[i].height = barSize.height + (unfoldEvents ? span.events.height : 0)
            ticks[i].hasBottleneck = _.reduce(
                spans.filter(span => span.tick === tickId),
                (meno, span) => {
                    return span.hasBottleneck
                },
                false,
            )

            odd = !odd
        }

        dimensions.height = d3.sum(_.pluck(ticks, 'height')) + margin.top + margin.bottom
        d3.select(svg).attr('height', dimensions.height)

        if (!scales || refreshScales) {
            scales = _scales(spans, ticks, selected)
        }

        if (selectedSpan && this.isZoomedIn() && !this.isSpanVisible(selectedSpan)) {
            this.resetZoom()
        }

        // http://bl.ocks.org/wrobstory/7612013
        if (!brush) {
            brush = d3.brushX(scales.x).on('end', () => this.brushEnd())

            d3.select(svg)
                .select('.button')
                .on('click', () => {
                    d3.event.stopPropagation()
                    this.resetZoom()
                    this.draw()
                })
        }

        // Reset zoom on new data or span selection:
        // d3.select(svg).select('.button').attr('class', 'button hidden')
        // scales.x.domain(scales.x2.domain())
        // brush.x(scales.x)

        d3.select(svg)
            .select('.brush')
            .call(brush)
            .selectAll('rect')
            .attr('height', dimensions.height - margin.top - margin.bottom)

        this.draw()
    }

    this.destroy = function() {
        svg = null
        brush = null
        spans = null
        ticks = null
        categories = null
        selected = null
        scales = null
    }
}

export default Chart
